// LEGĂTURILE dintre paginile structurii — un singur loc care știe cum arată
// un URL intern.
//
// Calea poartă UN SINGUR id, al rândului privit, iar corpul e un parametru
// (contractul «soluțiile pe faze», §8):
//
//   studio            → /p/${pid}/structura/${sol}
//   step/<section>    → /p/${pid}/structura/${sol}/pas/${section}
//   pipeline          → /p/${pid}/structura/${sol}?vedere=lant
//   prefs             → /p/${pid}/structura/${sol}?vedere=setari
//   fundatie          → /p/${pid}/structura/${sol}?vedere=fundatie
//   element/<ref>     → /p/${pid}/structura/${sol}/element/${ref}
//   elemente          → /p/${pid}/structura/${sol}/pas/elemente
//   fisa              → /p/${pid}/structura/${sol}/pas/fisa
//   verificari        → /p/${pid}/verificari/${sol}
//   verificare/<tip>  → /p/${pid}/verificari/${sol}/tip/${tip}
//   retete            → /p/${pid}/costuri/${sol}
//   solutii           → /p/${pid}/solutii
//   dwg-viewer/<file> → /p/${pid}/dossier/dwg/${fileId}
//   input             → /p/${pid}/dossier
//
// Fără `sol`, linkul duce pe segmentul gol al structurii (`/p/${pid}/structura`),
// care se rezolvă pe firul implicit.
//
// REFERINȚA DE LEGĂTURĂ. Paginile portate trec din mână în mână un singur șir,
// `solutie`, și îl dau înapoi constructorilor de mai jos. Șirul e id-ul
// rândului, urmat de `~corp` când corpul s-a ales explicit (`<uuid>~C2`, v.
// `refLegatura`): așa trecerea de la un pas la altul nu pierde corpul, fără să
// se rescrie cele o sută de chemări. Un uuid nu conține «~».
//
// Numele funcțiilor exportate și convențiile de query (`?focus=`, `?nivel=`)
// sunt cele de dinainte, ca paginile să nu se schimbe.
//
// Până acum fiecare pagină își scria hash-ul de mână, iar verificările picate
// erau enumerate ca numere («33 × amplasare_grinda_in_camera») fără vreun drum
// spre elementul din clădire. Ca să existe „click pe verificarea picată →
// pasul care o emite, cu elementul selectat", trebuie ca UN SINGUR modul să
// cunoască:
//
//   * lista pașilor și titlurile lor;
//   * forma URL-ului, cu cei trei parametri care se propagă: soluția (și
//     corpul), `focus` (elementul de selectat) și `nivel` (tabul de etaj, când
//     pagina are unul).
//
// ANCORA («unde se vede verificarea asta») vine de la SERVER, pe fiecare
// verificare picată — v. `optimizare.ancora_verificare`. Interfața nu are voie
// s-o reconstruiască din `element`: formatul diferă de la pas la pas și s-ar
// strica tăcut la prima schimbare.

// FOCUSUL cerut de un link, așa cum ajunge la pagina unui pas: elementul de
// selectat, etajul lui și un contor de navigare. Contorul există fiindcă al
// doilea click pe ACELAȘI rând trebuie să reaplice selecția — fără el, o
// stare identică nu declanșează niciun efect și linkul pare mort.
import { t } from "../i18n";
import type { Cheie } from "../i18n";

export type Focus = { ref: string; nivel: number | null; n: number };

export type Ancora = {
  // cheia pasului care emite verificarea (aceeași cu cheia din STEPS)
  pas?: string | null;
  // referința pe care pagina pasului o recunoaște: „GR1.1", „ST11.3",
  // „Ax 4" sau id-ul verificării la verdictele globale
  ref?: string | null;
  // etajul (1-based), când pagina are taburi de nivel
  nivel?: number | null;
};

// Pașii lanțului, în ordine. Un pas nou = o intrare aici + o componentă de
// rezultat (v. StepGeo / StepFundatie / StepRezistenta).
// NUMĂRUL pasului și NUMELE lui stau separat, nu lipite într-un titlu gata
// făcut: «Pasul 13 — Armarea grinzilor» se compune la afișare, fiindcă în
// altă limbă cuvântul «Pasul» e altul, iar eticheta scurtă se tăia până acum
// cu o expresie regulată peste textul românesc.
export const STEPS: { key: string; nr: number; nume: Cheie }[] = [
  { key: "studiu_geotehnic", nr: 1, nume: "struct.pas.studiu_geotehnic" },
  { key: "analiza_arhitectura", nr: 2, nume: "struct.pas.analiza_arhitectura" },
  { key: "strategie_arhitecturala", nr: 3, nume: "struct.pas.strategie_arhitecturala" },
  { key: "model_structura", nr: 4, nume: "struct.pas.model_structura" },
  { key: "fundatie_estimata", nr: 5, nume: "struct.pas.fundatie_estimata" },
  { key: "rezistenta_fundatie", nr: 6, nume: "struct.pas.rezistenta_fundatie" },
  { key: "model_fem", nr: 7, nume: "struct.pas.model_fem" },
  { key: "incarcari", nr: 8, nume: "struct.pas.incarcari" },
  { key: "seism", nr: 9, nume: "struct.pas.seism" },
  { key: "deplasari_laterale", nr: 10, nume: "struct.pas.deplasari_laterale" },
  { key: "combinatii", nr: 11, nume: "struct.pas.combinatii" },
  { key: "eforturi", nr: 12, nume: "struct.pas.eforturi" },
  { key: "armare_grinzi", nr: 13, nume: "struct.pas.armare_grinzi" },
  { key: "armare_pereti", nr: 14, nume: "struct.pas.armare_pereti" },
  { key: "armare_stalpi", nr: 15, nume: "struct.pas.armare_stalpi" },
  { key: "armare_placa", nr: 16, nume: "struct.pas.armare_placa" },
  { key: "verificare_fundatie", nr: 17, nume: "struct.pas.verificare_fundatie" },
  { key: "armare_fundatie", nr: 18, nume: "struct.pas.armare_fundatie" },
  { key: "optimizare_structura", nr: 19, nume: "struct.pas.optimizare_structura" },
];

const PE_CHEIE: Record<string, (typeof STEPS)[number]> = Object.fromEntries(
  STEPS.map((s) => [s.key, s])
);

// „Pasul 13 — Armarea grinzilor" din cheia pasului; pe cheia necunoscută
// întoarce chiar cheia, ca linkul să rămână vizibil (și reparabil), nu gol
export function titluPas(key?: string | null): string {
  const pas = PE_CHEIE[String(key ?? "")];
  if (!pas) return String(key ?? "");
  return t("struct.pas.titlu", { nr: pas.nr, nume: t(pas.nume) });
}

// „Pasul 13" — eticheta SCURTĂ, pentru butoanele din tabele înguste
export function pasScurt(key?: string | null): string {
  const pas = PE_CHEIE[String(key ?? "")];
  return pas ? t("struct.pas.scurt", { nr: pas.nr }) : String(key ?? "");
}


/** Referința de legătură a unui rând privit: id-ul, plus corpul când s-a ales
 *  explicit. */
export function refLegatura(sol: string | null | undefined,
                            corp?: string | null): string | null {
  if (!sol) return null;
  return corp ? `${sol}~${corp}` : sol;
}

/** Referința desfăcută: rândul și corpul. */
export function citesteRef(ref: string | null | undefined):
    { sol: string | null; corp: string | null } {
  if (!ref) return { sol: null, corp: null };
  const i = ref.indexOf("~");
  return i < 0 ? { sol: ref, corp: null } : { sol: ref.slice(0, i), corp: ref.slice(i + 1) || null };
}

function cuQuery(cale: string, q: URLSearchParams): string {
  const s = q.toString();
  return s ? `${cale}?${s}` : cale;
}

/** Parametrii comuni: corpul referinței, plus ce mai cere linkul. */
function parametri(ref: string | null | undefined,
                   alti?: Record<string, string | null | undefined>): URLSearchParams {
  const q = new URLSearchParams();
  const { corp } = citesteRef(ref);
  if (corp) q.set("corp", corp);
  for (const [k, v] of Object.entries(alti ?? {})) if (v != null && v !== "") q.set(k, v);
  return q;
}

/** Rădăcina structurii pe rândul referinței; fără rând, segmentul gol. */
function radacina(id: string, ref: string | null | undefined): string {
  const { sol } = citesteRef(ref);
  return sol ? `/p/${id}/structura/${encodeURIComponent(sol)}` : `/p/${id}/structura`;
}

// Calea unei secțiuni de proiect, purtând (opțional) soluția vizualizată și
// elementul de focalizat. TOATE linkurile interne trec pe aici, ca navigarea
// între pași să nu piardă soluția și corpul.
//
// Paginile portate spun `hashSectiune(id, "verificari")` peste tot, deci
// funcția e dispecerul: cheile pașilor (și paginile găzduite de pași, «acasa»,
// «imbunatatiri», «elemente», «fisa») merg pe `/pas/<secțiune>`, iar
// secțiunile promovate pe rutele lor.
export function hashSectiune(
  id: string,
  section: string,
  solutie?: string | null,
  focus?: { ref?: string | null; nivel?: number | null } | null
): string {
  switch (section) {
    case "pipeline":
      return hashPipeline(id, solutie);
    case "elemente":
      return hashElemente(id, solutie);
    case "studio":
      return hashStudio(id, solutie);
    case "fundatie":
      // cardul capitolului 4: Studioul deschis direct pe planul fundației
      return cuQuery(radacina(id, solutie), parametri(solutie, { vedere: "fundatie" }));
    case "verificari":
      return hashVerificari(id, solutie);
    case "retete":
      return hashRetete(id, solutie);
    case "prefs":
      return hashPrefs(id, solutie);
    case "solutii":
    case "ramuri":
      return hashSolutii(id);
    case "fisa":
      return hashFisa(id, solutie);
    case "plan_armare":
      return hashPlanArmare(id, solutie);
    case "plan_cofraj":
      return hashPlanCofraj(id, solutie);
    case "plan_sapatura":
      return hashPlanSapatura(id, solutie);
    case "input":
      return hashInput(id);
  }
  return cuQuery(`${radacina(id, solutie)}/pas/${encodeURIComponent(section)}`,
                 parametri(solutie, {
                   focus: focus?.ref ? String(focus.ref) : null,
                   nivel: focus?.nivel != null ? String(focus.nivel) : null,
                 }));
}

// O stare ÎNGHEȚATĂ a Pasului 4: pagina pasului cu `?bp=<k>` — o parte a
// rezultatului pasului, nu o soluție (contract §6).
export function hashBreakpoint(id: string, solutie: string | null | undefined,
                               k: string | number): string {
  return cuQuery(`${radacina(id, solutie)}/pas/model_structura`,
                 parametri(solutie, { bp: String(k) }));
}

// Calea PAGINII DE ELEMENT (obiectul navigabil, nu `?focus=` peste un pas):
// `/p/<uuid>/structura/<sol>/element/GR1.2`, purtând corpul ca orice link.
export function hashElement(
  id: string,
  ref: string,
  solutie?: string | null
): string {
  return cuQuery(`${radacina(id, solutie)}/element/${encodeURIComponent(ref)}`,
                 parametri(solutie));
}

// Calea PAGINII UNEI MĂRCI de bară / etrier (`3g`, `e1` — v. `marci.ts`):
// `/p/<uuid>/structura/<sol>/armatura/e1`, purtând corpul ca orice link.
export function hashMarca(
  id: string,
  marca: string,
  solutie?: string | null
): string {
  return cuQuery(`${radacina(id, solutie)}/armatura/${encodeURIComponent(marca)}`,
                 parametri(solutie));
}

/** «Componente»: o vedere a Studioului (`?vedere=componente`), pe tabul
 *  familiei când `tab` e dat (`?comp=`). */
export function hashComponente(id: string, solutie?: string | null,
                               tab?: string | null): string {
  return cuQuery(radacina(id, solutie),
                 parametri(solutie, { vedere: "componente", comp: tab }));
}

/** «Plan de armare»: o vedere a Studioului (`?vedere=plan_armare`), deschisă
 *  pe o coală anume când `coala` e dat. */
export function hashPlanArmare(id: string, solutie?: string | null,
                               coala?: string | null): string {
  return cuQuery(radacina(id, solutie),
                 parametri(solutie, { vedere: "plan_armare", coala }));
}

/** «Plan de cofraj»: o vedere a Studioului (`?vedere=plan_cofraj`), deschisă
 *  pe coala unui nivel când `coala` e dat («cofraj:etaj 3»). */
export function hashPlanCofraj(id: string, solutie?: string | null,
                               coala?: string | null): string {
  return cuQuery(radacina(id, solutie),
                 parametri(solutie, { vedere: "plan_cofraj", coala }));
}

/** «Plan de săpătură»: o vedere a Studioului (`?vedere=plan_sapatura`),
 *  deschisă pe o coală când `coala` e dată («sapatura:plan»). */
export function hashPlanSapatura(id: string, solutie?: string | null,
                                 coala?: string | null): string {
  return cuQuery(radacina(id, solutie),
                 parametri(solutie, { vedere: "plan_sapatura", coala }));
}

// Calea PAGINII TIPULUI DE VERIFICARE (catalogul primește URL):
// `/p/<uuid>/verificari/<sol>/tip/nod_forfecare`.
export function hashVerificare(
  id: string,
  tip: string,
  solutie?: string | null
): string {
  const { sol } = citesteRef(solutie);
  const baza = sol ? `/p/${id}/verificari/${encodeURIComponent(sol)}` : `/p/${id}/verificari`;
  return cuQuery(`${baza}/tip/${encodeURIComponent(tip)}`, parametri(solutie));
}

// --- restul rutelor din tabelă, ca linkurile să nu se scrie de mână --------

export function hashStudio(id: string, solutie?: string | null): string {
  return cuQuery(radacina(id, solutie), parametri(solutie, { vedere: "model" }));
}

/** «Lanțul de calcul»: o vedere a structurii, nu o pagină a șinei (A2). */
export function hashPipeline(id: string, solutie?: string | null): string {
  return cuQuery(radacina(id, solutie), parametri(solutie, { vedere: "lant" }));
}

export function hashElemente(id: string, solutie?: string | null): string {
  return cuQuery(`${radacina(id, solutie)}/pas/elemente`, parametri(solutie));
}

export function hashVerificari(id: string, solutie?: string | null): string {
  const { sol } = citesteRef(solutie);
  return cuQuery(sol ? `/p/${id}/verificari/${encodeURIComponent(sol)}` : `/p/${id}/verificari`,
                 parametri(solutie));
}

export function hashRetete(id: string, solutie?: string | null): string {
  const { sol } = citesteRef(solutie);
  // corpul merge și aici: devizul unui corp e al lui, al «toate corpurile»
  // e al soluției — cu groapa întregului complex
  return cuQuery(sol ? `/p/${id}/costuri/${encodeURIComponent(sol)}` : `/p/${id}/costuri`,
                 parametri(solutie));
}

/** «Setări de calcul»: o vedere a structurii (A2). Preferințele sunt ale
 *  proiectului, dar se schimbă în timp ce te uiți la o soluție. */
export function hashPrefs(id: string, solutie?: string | null): string {
  return cuQuery(radacina(id, solutie), parametri(solutie, { vedere: "setari" }));
}

export function hashSolutii(id: string): string {
  return `/p/${id}/solutii`;
}

// fișa unei soluții de structură: verdictele Pasului 19 ale rândului
export function hashFisa(id: string, solutie?: string | null): string {
  return cuQuery(`${radacina(id, solutie)}/pas/fisa`, parametri(solutie));
}

export function hashInput(id: string): string {
  return `/p/${id}/dossier`;
}

// Ruta vizualizatorului de desene: platforma adresează fișierul prin id-ul
// lui (UUID), nu prin nume.
export function viewerHash(projectId: string, fileId: string): string {
  return `/p/${projectId}/dossier/dwg/${encodeURIComponent(fileId)}`;
}

// Linkul unei verificări picate: unde se vede și ce scrie pe buton.
// `null` = verificarea nu are pas cunoscut (nu se poate naviga nicăieri).
export function linkAncora(
  projectId: string,
  solutie: string | null | undefined,
  ancora?: Ancora | null
): { href: string; eticheta: string; titlu: string } | null {
  const pas = ancora?.pas;
  if (!pas || !PE_CHEIE[pas]) return null;
  const ref = ancora?.ref ?? null;
  return {
    href: hashSectiune(projectId, pas, solutie ?? null, {
      ref,
      nivel: ancora?.nivel ?? null,
    }),
    eticheta: ref ? `${pasScurt(pas)} · ${ref}` : pasScurt(pas),
    titlu: ref
      ? t("struct.pas.link_cu_ref", { pas: titluPas(pas), ref })
      : t("struct.pas.link_verificare", { pas: titluPas(pas) }),
  };
}

// Id-ul de plan COMPLET al unui element din model, adică cel pe care îl
// recunosc pașii de armare și indexul de elemente: „GR47.2", „ST11.3".
//
// Capcană («zice la toate elementele că elementul n-are
// verificări»): `model.grinzi[].id_plan` / `model.stalpi[].id_plan` conțin
// DEJA sufixul de nivel, deși au și `etaj` alături. Cine adăuga `.${etaj+1}`
// obținea „ST1.1.1", care nu se potrivea cu nimic — nici cu verificările,
// nici cu extrasul de armătură, deci elementul apărea fără verdicte și cu
// cost zero. Aici e singurul loc care decide forma: sufixul se adaugă DOAR
// dacă lipsește.
export function refCuNivel(
  idPlan?: string | null,
  etaj?: number | null
): string | null {
  const id = String(idPlan ?? "").trim();
  if (!id) return null;
  if (id.includes(".")) return id;
  return `${id}.${(etaj ?? 0) + 1}`;
}

// Nivelul (1-based) dintr-un id de plan cu sufix: „GR47.2" → 2, „ST11" → null.
// Îl folosesc paginile cu taburi de etaj ca să deschidă tabul corect când
// linkul poartă doar referința.
export function nivelDinRef(ref?: string | null): number | null {
  const m = String(ref ?? "").match(/\.(\d+)$/);
  return m ? Number(m[1]) : null;
}

// Id-ul de plan fără sufixul de nivel: „GR47.2" → „GR47". E cheia stivei la
// Pasul 15 (un stâlp = o stivă cu câte un rând pe nivel).
export function refFaraNivel(ref?: string | null): string {
  return String(ref ?? "").split(".")[0];
}

// PASUL care armează elementul cu id-ul de plan dat: „GR47.2" → Pasul 13,
// „ST11.3" → Pasul 15, „PB3" → Pasul 14. `null` = element care nu se armează
// nicăieri (de exemplu „PBB2", peretele de pe latura unui balcon, care intră
// în model doar cu greutatea lui).
//
// Există ca să poată fi făcut click pe un element în viewerul contextual
// «unde e în clădire»: dai click pe un stâlp din nota de calcul a unei grinzi
// și ajungi în pasul stâlpilor, cu stâlpul acela selectat.
export function pasElement(ref?: string | null): string | null {
  const r = String(ref ?? "");
  if (/^GR\d/.test(r)) return "armare_grinzi";
  if (/^ST\d/.test(r)) return "armare_stalpi";
  if (/^PB\d/.test(r)) return "armare_pereti";
  if (/^OP\d/.test(r)) return "armare_placa";   // ochi de placă: „OP3.1"
  // grinda de fundare a unui ax: „F-Ax 4" — id-ul pe care îl scrie deja
  // extrasul de armătură și cu care 3D-ul numește piesa
  if (/^F-/.test(r)) return "armare_fundatie";
  return null;
}
